import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Building2,
  Coins,
  MessageSquare,
  Shield,
  ToggleLeft,
  Settings2,
  Bot,
  Package,
  BarChart3,
  Wrench,
  HelpCircle,
  CreditCard,
  Mail,
  type LucideIcon,
} from "lucide-react";
import {
  ADMIN_HUB_SECTIONS,
  ADMIN_TOOL_LINKS,
  OVERFLOW_CATEGORIES,
} from "@/lib/firm-navigation-catalog";

const ICON_MAP: Record<string, LucideIcon> = {
  Users,
  Building2,
  Coins,
  MessageSquare,
  Shield,
  ToggleLeft,
  Settings2,
  Bot,
  Package,
  BarChart3,
  Wrench,
  HelpCircle,
  CreditCard,
  Mail,
};

function HubIcon({ name }: { name: string }) {
  const Icon = ICON_MAP[name] ?? Settings2;
  return <Icon className="h-4 w-4 text-gold shrink-0" />;
}

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Firm Admin · AVS ERP" },
      { name: "description", content: "Firm administration hub — users, billing, communications, and setup." },
    ],
  }),
  component: AdminHome,
});

function AdminHome() {
  return (
    <div className="space-y-6" data-testid="admin-hub">
      <div className="rounded-xl border border-gold/20 bg-gradient-to-br from-gold/5 via-card to-card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h1 className="text-lg font-bold font-serif text-foreground">Firm Admin</h1>
          <Badge variant="outline" className="text-[10px]">Alt+A</Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Company setup, team access, communications, credits, automations, and integrations.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {ADMIN_HUB_SECTIONS.map((section) => (
          <Card key={section.id} className="p-4 space-y-3">
            <div>
              <h2 className="font-semibold text-sm">{section.title}</h2>
              <p className="text-xs text-muted-foreground">{section.description}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {section.items.map((item) => (
                <Link key={`${section.id}-${item.label}`} to={item.to as "/"}>
                  <div className="rounded-lg border border-border/70 p-3 h-full hover:border-gold/40 hover:bg-gold/5 transition-colors">
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <HubIcon name={item.iconName} />
                      {item.label}
                    </div>
                    {item.note ? (
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{item.note}</p>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4 space-y-3">
        <h2 className="font-semibold text-sm">Quick links</h2>
        <div className="flex flex-wrap gap-2">
          {OVERFLOW_CATEGORIES.flatMap((c) => c.items).map((item) => (
            <Link
              key={item.id}
              to={item.to as "/"}
              search={(item.search ?? undefined) as never}
              className="text-xs rounded-full border border-border px-3 py-1 hover:border-gold/40 hover:bg-gold/5"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <h2 className="font-semibold text-sm">Tools</h2>
        <div className="flex flex-wrap gap-2">
          {ADMIN_TOOL_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to as "/"}
              className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1 hover:border-gold/40 hover:bg-gold/5"
            >
              <HubIcon name={item.iconName} />
              {item.label}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
